/**
 * ToothChart — FDI dental odontogram modal.
 *
 * Permanent dentition: FDI 11–48, 32 teeth (4 quadrants × 8).
 * Primary dentition:   FDI 51–85, 20 teeth (4 quadrants × 5).
 *
 * Tooth indicators (dots, M-badge) are absolutely positioned within each
 * fixed-width cell so they never push neighbouring teeth.
 */

import React, { useState, useCallback } from "react";
import { DENTAL_CONDITIONS } from "../../constants/dental.constants";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ToothFinding {
  tooth: number;
  conditions: string[];
  mobility_grade: number | null;
  note?: string;
}

export interface ToothChartProps {
  isOpen: boolean;
  onClose: () => void;
  findings: ToothFinding[];
  onChange: (findings: ToothFinding[]) => void;
  readOnly?: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const STANDARD_CONDITIONS = [
  { key: "caries",                label: "Caries",                color: "#f97316" },
  { key: "reversible_pulpitis",   label: "Reversible pulpitis",   color: "#eab308" },
  { key: "irreversible_pulpitis", label: "Irreversible pulpitis", color: "#ef4444" },
  { key: "stain",                 label: "Stain",                 color: "#64748b" },
  { key: "pyorrhea",              label: "Pyorrhea",              color: "#a855f7" },
  { key: "recession",             label: "Recession",             color: "#0ea5e9" },
  { key: "root_canal_treated",    label: "Root canal treated",    color: "#3b82f6" },
  { key: "missing",               label: "Missing",               color: "#94a3b8" },
  { key: "crown",                 label: "Crown",                 color: "#10b981" },
  { key: "implant",               label: "Implant",               color: "#1e293b" },
] as const;

const COL_LEFT  = ["caries", "irreversible_pulpitis", "pyorrhea", "root_canal_treated", "crown"];
const COL_RIGHT = ["reversible_pulpitis", "stain", "recession", "missing", "implant"];
const CONDITION_MAP = Object.fromEntries(STANDARD_CONDITIONS.map((c) => [c.key, c]));
// Colour lookup for display covers every condition the inline chart can
// record; CONDITION_MAP stays the editable popup's set (and the text parser's).
const DISPLAY_CONDITIONS: readonly { key: string; label: string; color: string }[] = DENTAL_CONDITIONS;
const DISPLAY_MAP = Object.fromEntries(DISPLAY_CONDITIONS.map((c) => [c.key, c]));

// Position names — permanent teeth use index 1-8, primary use 1-5
const PERM_POSITIONS  = ["", "Central incisor", "Lateral incisor", "Canine", "First premolar", "Second premolar", "First molar", "Second molar", "Third molar"];
const PRIM_POSITIONS  = ["", "Central incisor", "Lateral incisor", "Canine", "First molar", "Second molar"];

const QUADRANT_LABELS: Record<number, string> = {
  1: "Upper right", 2: "Upper left", 3: "Lower left", 4: "Lower right",
  5: "Upper right", 6: "Upper left", 7: "Lower left", 8: "Lower right",
};

// Permanent dentition quadrants
const UPPER_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11];
const UPPER_LEFT  = [21, 22, 23, 24, 25, 26, 27, 28];
const LOWER_RIGHT = [48, 47, 46, 45, 44, 43, 42, 41];
const LOWER_LEFT  = [31, 32, 33, 34, 35, 36, 37, 38];

// Primary dentition quadrants
const P_UPPER_RIGHT = [55, 54, 53, 52, 51];
const P_UPPER_LEFT  = [61, 62, 63, 64, 65];
const P_LOWER_RIGHT = [85, 84, 83, 82, 81];
const P_LOWER_LEFT  = [71, 72, 73, 74, 75];

type Dentition = "permanent" | "primary";

// ─── Clinical text generator (exported for auto-populating chief complaint) ─

// Regex matches "Dental Findings:\n" followed by bullet lines.
// Accepts "• ", "- ", "* " bullets for backward compat with saved data.
const DENTAL_BLOCK_RE = /Dental Findings:\n(?:[-•*] [^\n]*\n?)*/;

const MOBILITY_ROMAN = ["", "I", "II", "III"];

// Clinical-friendly condition labels
const CLINICAL_LABELS: Record<string, string> = {
  caries: "Caries",
  reversible_pulpitis: "Reversible pulpitis",
  irreversible_pulpitis: "Irreversible pulpitis",
  stain: "Staining",
  pyorrhea: "Pyorrhea",
  recession: "Gingival recession",
  root_canal_treated: "Root canal treated",
  missing: "Missing tooth",
  crown: "Crown",
  implant: "Implant",
  // Charted only from the inline chart; the parser keeps the original ten,
  // and visits saved with these restore from visits.dental_chart instead.
  deep_caries: "Deep caries",
  periapical_abscess: "Periapical abscess",
  fracture: "Fracture",
  impacted: "Impacted",
  root_stump: "Root stump",
  attrition: "Attrition / abrasion",
};

/**
 * Build clinical-quality text from tooth findings.
 *
 * Example output:
 *   Dental Findings:
 *   - Tooth 12 (Upper right lateral incisor) - Caries, Staining. Mobility Grade II.
 *   - Tooth 33 (Lower left canine) - Caries.
 */
export function buildDentalFindingsText(findings: ToothFinding[]): string {
  const active = findings.filter((f) => f.conditions.length > 0 || f.mobility_grade);
  if (active.length === 0) return "";

  const lines = active.map((f) => {
    const toothLabel = getToothName(f.tooth);          // e.g. "upper right lateral incisor"
    const condNames = f.conditions
      .map((c) => CLINICAL_LABELS[c] ?? c)
      .filter(Boolean);

    let line = `- Tooth ${f.tooth} (${toothLabel})`;

    if (condNames.length > 0) {
      line += ` - ${condNames.join(", ")}`;
    }

    if (f.mobility_grade) {
      line += condNames.length > 0 ? ". " : " - ";
      line += `Mobility Grade ${MOBILITY_ROMAN[f.mobility_grade] ?? f.mobility_grade}`;
    }

    return line + ".";
  });

  return `Dental Findings:\n${lines.join("\n")}`;
}

/**
 * Merge a dental-findings block into an existing chief-complaint string.
 * Replaces any previous "Dental Findings:" block; preserves free-text the
 * doctor typed outside the block.
 */
export function mergeDentalBlock(existing: string, findings: ToothFinding[]): string {
  const cleaned = existing.replace(DENTAL_BLOCK_RE, "").trim();
  const block = buildDentalFindingsText(findings);

  if (!block) return cleaned;
  return cleaned ? `${cleaned}\n\n${block}` : block;
}

// ─── Parser (reverse of buildDentalFindingsText) ───────────────────────────

const REVERSE_CLINICAL: Record<string, string> = Object.fromEntries(
  Object.entries(CLINICAL_LABELS).map(([k, v]) => [v.toLowerCase(), k]),
);

// Lenient: accepts "•", "-", "*" bullets and "-", "–", "—" separators
// so doctor edits and PDF round-trips don't silently drop findings.
const FINDING_LINE_RE = /[-•*] Tooth (\d+)\s*\([^)]*\)\s*[-–—]\s*(.+)/;

/**
 * Parse the "Dental Findings:" block from chief-complaint text back into
 * structured ToothFinding[].  Used by the read-only document viewer.
 */
export function parseDentalFindingsText(text: string): ToothFinding[] {
  if (!text) return [];
  const blockMatch = text.match(/Dental Findings:\n((?:[-•*] [^\n]*\n?)*)/);
  if (!blockMatch) return [];

  const findings: ToothFinding[] = [];
  const lines = blockMatch[1].split("\n").filter(Boolean);

  for (const line of lines) {
    const m = line.match(FINDING_LINE_RE);
    if (!m) continue;

    const tooth = parseInt(m[1], 10);
    const rest = m[2];

    // Extract mobility grade
    let mobility_grade: number | null = null;
    const mobMatch = rest.match(/Mobility Grade (I{1,3}|\d)/);
    if (mobMatch) {
      const roman: Record<string, number> = { I: 1, II: 2, III: 3 };
      const parsed = parseInt(mobMatch[1], 10);
      mobility_grade = roman[mobMatch[1]] ?? (Number.isNaN(parsed) ? null : parsed);
    }

    // Extract conditions (everything before ". Mobility" or trailing ".")
    const condPart = rest.replace(/\.?\s*Mobility Grade.*/, "").replace(/\.$/, "").trim();
    const conditions = condPart
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .map((s) => REVERSE_CLINICAL[s] ?? s)
      .filter((s) => s && CONDITION_MAP[s]);

    findings.push({ tooth, conditions, mobility_grade });
  }

  return findings;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getToothName(fdi: number): string {
  const q = Math.floor(fdi / 10);
  const p = fdi % 10;
  const names = q >= 5 ? PRIM_POSITIONS : PERM_POSITIONS;
  return `${QUADRANT_LABELS[q] ?? ""} ${names[p] ?? ""}`.trim().toLowerCase();
}

type ToothType = "incisor" | "canine" | "premolar" | "molar";
function toothType(fdi: number): ToothType {
  const q = Math.floor(fdi / 10);
  const p = fdi % 10;
  // Primary teeth: positions 4,5 are molars (no premolars)
  if (q >= 5) {
    if (p <= 2) return "incisor";
    if (p === 3) return "canine";
    return "molar";
  }
  if (p <= 2) return "incisor";
  if (p === 3) return "canine";
  if (p <= 5) return "premolar";
  return "molar";
}

function isUpper(fdi: number): boolean {
  const q = Math.floor(fdi / 10);
  return q === 1 || q === 2 || q === 5 || q === 6;
}

// ─── SVG Tooth Shapes ───────────────────────────────────────────────────────
// viewBox="0 0 28 48" — upper orientation (roots up, crown down)

const UPPER_PATHS: Record<ToothType, string> = {
  incisor:  "M12 2 L11 20 L5 24 L5 40 Q5 48 14 48 Q23 48 23 40 L23 24 L17 20 L16 2 Z",
  canine:   "M12 0 L11 22 L5 26 L5 40 Q5 48 14 48 Q23 48 23 40 L23 26 L17 22 L16 0 Z",
  premolar: "M7 2 L9 18 L4 24 L4 40 Q4 48 14 48 Q24 48 24 40 L24 24 L19 18 L21 2 L17 8 L14 14 L11 8 Z",
  molar:    "M4 2 L7 16 L2 22 L2 40 Q2 48 14 48 Q26 48 26 40 L26 22 L21 16 L24 2 L20 8 L17 14 L14 6 L11 14 L8 8 Z",
};
const LOWER_MOLAR_PATH = "M7 2 L9 18 L3 24 L3 40 Q3 48 14 48 Q25 48 25 40 L25 24 L19 18 L21 2 L17 8 L14 14 L11 8 Z";

// ─── Dot slot positions along the left/right edges of the tooth ─────────────
// Rectangular distribution: alternating left-edge / right-edge, top to bottom.
// With overflow="visible" on the SVG, dots at x=-2 / x=30 render just outside
// the tooth path on the cell border.  The scale(1,-1) flip for lower teeth
// mirrors these vertically automatically.
const DOT_SLOTS: [number, number][] = [
  [-2,  4],   // left  — top
  [30,  4],   // right — top
  [-2, 14],   // left  — upper-mid
  [30, 14],   // right — upper-mid
  [-2, 24],   // left  — mid
  [30, 24],   // right — mid
  [-2, 34],   // left  — lower-mid
  [30, 34],   // right — lower-mid
  [-2, 44],   // left  — bottom
  [30, 44],   // right — bottom
];

// ─── Tooth SVG Icon ─────────────────────────────────────────────────────────

interface ToothIconProps {
  fdi: number;
  finding: ToothFinding | undefined;
  isSelected: boolean;
  onClick: () => void;
  readOnly?: boolean;
}

const ToothIcon: React.FC<ToothIconProps> = ({ fdi, finding, isSelected, onClick, readOnly }) => {
  const upper = isUpper(fdi);
  const type = toothType(fdi);
  const hasConds = !!(finding && (finding.conditions.length > 0 || finding.mobility_grade));

  const path = !upper && type === "molar" ? LOWER_MOLAR_PATH : UPPER_PATHS[type];
  const strokeColor = hasConds ? "#3b82f6" : "#94a3b8";
  const fillColor = hasConds ? "#eff6ff" : "none";

  // One dot per checked condition (no dedup — each condition gets its own dot)
  const dots: string[] = [];
  if (finding) {
    for (const c of finding.conditions) {
      const col = DISPLAY_MAP[c]?.color;
      if (col) dots.push(col);
    }
  }

  return (
    <div
      className={`relative flex flex-col items-center justify-center rounded-lg w-9 sm:w-10 py-1 transition-colors ${
        readOnly ? "" : "cursor-pointer"
      } ${isSelected ? "ring-2 ring-blue-300 bg-blue-50/40" : readOnly ? "" : "hover:bg-slate-50"}`}
      style={{ minHeight: 68 }}
      onClick={readOnly ? undefined : onClick}
      role={readOnly ? undefined : "button"}
      aria-label={`Tooth ${fdi}`}
    >
      {/* Tooth SVG — dots + mobility badge rendered inside as SVG circles */}
      <svg viewBox="0 0 28 48" overflow="visible" className="w-7 h-10 sm:w-8 sm:h-11" aria-hidden>
        <g transform={upper ? undefined : "scale(1,-1) translate(0,-48)"}>
          {/* Tooth outline */}
          <path d={path} stroke={strokeColor} strokeWidth={1.5} fill={fillColor} strokeLinejoin="round" />

          {/* Condition dots — distributed along left/right edges */}
          {dots.map((color, i) => {
            const slot = DOT_SLOTS[i % DOT_SLOTS.length];
            return <circle key={i} cx={slot[0]} cy={slot[1]} r={2.5} fill={color} />;
          })}

          {/* Mobility grade badge — center top */}
          {finding?.mobility_grade && (
            <g>
              <rect x="5" y="-10" width="18" height="10" rx="3" fill="#ef4444" />
              <text x="14" y="-2" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" fontFamily="system-ui">
                M{finding.mobility_grade}
              </text>
            </g>
          )}
        </g>
      </svg>

      {/* FDI number */}
      <span className={`text-[11px] leading-none ${hasConds ? "font-bold text-blue-700" : "text-slate-500"}`}>
        {fdi}
      </span>
    </div>
  );
};

// ─── Condition Popover ──────────────────────────────────────────────────────

interface ConditionPopoverProps {
  fdi: number;
  finding: ToothFinding;
  onUpdate: (f: ToothFinding) => void;
  onClear: () => void;
  onClose: () => void;
}

const ConditionPopover: React.FC<ConditionPopoverProps> = ({ fdi, finding, onUpdate, onClear, onClose }) => {
  const toggleCondition = (key: string) => {
    const next = finding.conditions.includes(key)
      ? finding.conditions.filter((c) => c !== key)
      : [...finding.conditions, key];
    onUpdate({ ...finding, conditions: next });
  };

  const setMobility = (grade: number) => {
    onUpdate({ ...finding, mobility_grade: finding.mobility_grade === grade ? null : grade });
  };

  const renderCol = (keys: string[]) =>
    keys.map((key) => {
      const cond = CONDITION_MAP[key];
      if (!cond) return null;
      return (
        <label key={key} className="flex items-center gap-2 cursor-pointer py-0.5">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: cond.color }} />
          <input
            type="checkbox"
            checked={finding.conditions.includes(key)}
            onChange={() => toggleCondition(key)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-slate-700 select-none">{cond.label}</span>
        </label>
      );
    });

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-4 w-full max-w-md">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Tooth {fdi}</h3>
          <p className="text-sm text-blue-600 capitalize">{getToothName(fdi)}</p>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 mb-4">
        <div className="flex flex-col">{renderCol(COL_LEFT)}</div>
        <div className="flex flex-col">{renderCol(COL_RIGHT)}</div>
      </div>

      <div className="flex items-center gap-3 mb-4 pt-2 border-t border-slate-100">
        <span className="text-sm font-medium text-slate-700">Mobility grade</span>
        <div className="flex gap-1">
          {[1, 2, 3].map((g) => (
            <button
              key={g}
              onClick={() => setMobility(g)}
              className={`w-8 h-8 rounded-lg text-sm font-semibold transition-colors ${
                finding.mobility_grade === g
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <button onClick={onClear} className="text-sm text-slate-500 hover:text-slate-700 transition-colors">Clear tooth</button>
        <button onClick={onClose} className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">Done</button>
      </div>
    </div>
  );
};

// ─── Quadrant Component ─────────────────────────────────────────────────────

interface QuadrantProps {
  label: string;
  teeth: number[];
  findings: ToothFinding[];
  selectedTooth: number | null;
  onSelectTooth: (fdi: number) => void;
  readOnly?: boolean;
}

const Quadrant: React.FC<QuadrantProps> = ({ label, teeth, findings, selectedTooth, onSelectTooth, readOnly }) => (
  <div className="flex flex-col">
    <div className="text-center text-xs font-semibold text-blue-700 bg-blue-50 rounded-full py-1 px-3 mb-2 mx-auto" dangerouslySetInnerHTML={{ __html: label }} />
    <div className="flex justify-center">
      {teeth.map((fdi) => (
        <ToothIcon
          key={fdi}
          fdi={fdi}
          finding={findings.find((f) => f.tooth === fdi)}
          isSelected={selectedTooth === fdi}
          onClick={() => onSelectTooth(fdi)}
          readOnly={readOnly}
        />
      ))}
    </div>
  </div>
);

// ─── Chart Grids ────────────────────────────────────────────────────────────

const PermanentGrid: React.FC<{ findings: ToothFinding[]; selectedTooth: number | null; onSelect: (n: number) => void; readOnly?: boolean }> = ({ findings, selectedTooth, onSelect, readOnly }) => (
  <>
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Upper right &middot; 18&ndash;11" teeth={UPPER_RIGHT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Upper left &middot; 21&ndash;28" teeth={UPPER_LEFT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Lower right &middot; 48&ndash;41" teeth={LOWER_RIGHT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Lower left &middot; 31&ndash;38" teeth={LOWER_LEFT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
    </div>
  </>
);

const PrimaryGrid: React.FC<{ findings: ToothFinding[]; selectedTooth: number | null; onSelect: (n: number) => void; readOnly?: boolean }> = ({ findings, selectedTooth, onSelect, readOnly }) => (
  <>
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Upper right &middot; 55&ndash;51" teeth={P_UPPER_RIGHT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Upper left &middot; 61&ndash;65" teeth={P_UPPER_LEFT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Lower right &middot; 85&ndash;81" teeth={P_LOWER_RIGHT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
      <div className="border border-slate-100 rounded-xl p-3">
        <Quadrant label="Lower left &middot; 71&ndash;75" teeth={P_LOWER_LEFT} findings={findings} selectedTooth={selectedTooth} onSelectTooth={onSelect} readOnly={readOnly} />
      </div>
    </div>
  </>
);

// ─── Main Component ─────────────────────────────────────────────────────────

export const ToothChart: React.FC<ToothChartProps> = ({ isOpen, onClose, findings, onChange, readOnly }) => {
  const [selectedTooth, setSelectedTooth] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<Dentition>("permanent");

  const handleSelectTooth = useCallback((fdi: number) => {
    setSelectedTooth((prev) => (prev === fdi ? null : fdi));
  }, []);

  const getFinding = useCallback(
    (fdi: number): ToothFinding =>
      findings.find((f) => f.tooth === fdi) ?? { tooth: fdi, conditions: [], mobility_grade: null },
    [findings],
  );

  const handleUpdateFinding = useCallback(
    (updated: ToothFinding) => {
      const idx = findings.findIndex((f) => f.tooth === updated.tooth);
      const next = [...findings];
      if (idx >= 0) next[idx] = updated;
      else next.push(updated);
      onChange(next.filter((f) => f.conditions.length > 0 || f.mobility_grade));
    },
    [findings, onChange],
  );

  const handleClear = useCallback(() => {
    if (selectedTooth === null) return;
    onChange(findings.filter((f) => f.tooth !== selectedTooth));
  }, [selectedTooth, findings, onChange]);

  const handleClosePopover = useCallback(() => setSelectedTooth(null), []);

  const switchTab = (tab: Dentition) => {
    setSelectedTooth(null);
    setActiveTab(tab);
  };

  if (!isOpen) return null;

  const isPrimary = activeTab === "primary";
  const subtitle = isPrimary
    ? "Primary dentition \u00B7 FDI 51\u201385 \u00B7 20 teeth"
    : "Permanent dentition \u00B7 FDI 11\u201348 \u00B7 32 teeth";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[900px] my-8" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="flex items-start justify-between p-5 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C9 2 6 4 6 7c0 2 1 3.5 1.5 5.5C8 15 8.5 22 12 22s4-7 4.5-9.5C17 10.5 18 9 18 7c0-3-3-5-6-5z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Dental chart</h2>
              <p className="text-sm text-slate-500">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Close dental chart">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex items-center gap-1 px-5 pt-4 pb-2">
          <button
            onClick={() => switchTab("permanent")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              !isPrimary ? "text-slate-900 bg-slate-100" : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            Adult &middot; permanent
          </button>
          <button
            onClick={() => switchTab("primary")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              isPrimary ? "text-slate-900 bg-slate-100" : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            Child &middot; primary
          </button>
        </div>

        {/* ── Instruction ── */}
        {!readOnly && (
          <p className="text-sm text-blue-600 px-5 pb-3">
            Tap a tooth to record its condition.
          </p>
        )}

        {/* ── Legend (readOnly) ── */}
        {readOnly && (
          <div className="flex flex-wrap gap-3 px-5 pb-3">
            {DISPLAY_CONDITIONS.filter((c) => findings.some((f) => f.conditions.includes(c.key))).map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: c.color }} />
                {c.label}
              </span>
            ))}
          </div>
        )}

        {/* ── Chart Grid ── */}
        <div className="px-5 pb-4">
          {isPrimary ? (
            <PrimaryGrid findings={findings} selectedTooth={selectedTooth} onSelect={handleSelectTooth} readOnly={readOnly} />
          ) : (
            <PermanentGrid findings={findings} selectedTooth={selectedTooth} onSelect={handleSelectTooth} readOnly={readOnly} />
          )}
        </div>

        {/* ── Condition Popover ── */}
        {!readOnly && selectedTooth !== null && (
          <div className="px-5 pb-5">
            <div className="flex justify-center mb-2">
              <div className="w-px h-6 border-l border-dashed border-slate-300" />
            </div>
            <div className="flex justify-center">
              <ConditionPopover
                fdi={selectedTooth}
                finding={getFinding(selectedTooth)}
                onUpdate={handleUpdateFinding}
                onClear={handleClear}
                onClose={handleClosePopover}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToothChart;
